import DynamicSectionElement from '@/js/dynamic-section-element';
import { debounce } from '@/js/helpers';
import { fields } from '@/js/predictive-search';

class PredictiveSearchBar extends DynamicSectionElement {
  connectedCallback() {
    this.initVariables();
    this.registerEvents();
    this.setSearchLimit();
    this.input.focus();
  }

  /**
   * Initializes the variables
   * @return {void}
   */
  initVariables() {
    this.input = this.querySelector('[data-predictive-search-input]');
    this.template = this.dataset.template;
    this.searchTerms = this.dataset.searchTerms;
    this.tabs = document.querySelector('search-results-tab-content');

    if (this.template === 'search') {
      this.classList.add('active');
      this.input.value = this.searchTerms;
    }
    this.predictiveSearchResults = document.querySelector(
      '[data-predictive-search-results]'
    );
    this.closeSearchBarElement = document.querySelector(
      '[data-close-search-bar]'
    );
    this.iconMagnifierElement = document.querySelector('[data-search-icon]');
    this.mainContent = document.querySelector('#MainContent');

    this.closeSearchBar = this.closeSearchBar.bind(this);
    this.conditionallyCloseSearchBar =
      this.conditionallyCloseSearchBar.bind(this);
  }

  /**
   * Registers the events
   * @return {void}
   */
  registerEvents() {
    this.input.addEventListener(
      'keyup',
      debounce(event => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.closeSearchBarElement.addEventListener('click', () => {
      this.closeSearchBar();
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.close();
      }
    });

    this.addCloseOnClickedOutsideListener();
  }

  /**
   * Closes the search bar
   * @return {void}
   */
  closeSearchBar() {
    document.body.classList.remove('predictive-search-bar-open');
    this.iconMagnifierElement.classList.remove('fade');
    this.input.value = '';

    // If its not the search page, we hide the predictive search bar
    if (globalThis.location.pathname !== '/search') {
      this.classList.remove('active');
    }
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (searchTerm.length === 0) {
      this.predictiveSearchResults.close();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  /**
   * Uses section rendering to make an ajax request to return search related products
   * https://shopify.dev/api/ajax/reference/predictive-search
   * @param {string} searchTerm
   */
  getSearchResults(searchTerm) {
    // type & unavailable product settings are set in Search & Discovery App
    const limit = this.searchLimit;
    const limitScope = 'each';
    const sectionId = 'predictive-search-results';

    fetch(
      `/search/suggest?q=${searchTerm}&resources[limit]=${limit}&resources[limit_scope]=${limitScope}&resources[options][fields]=${fields}&section_id=${sectionId}`
    )
      .then(response => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.predictiveSearchResults.close();
          throw error;
        }

        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-predictive-search-results]').innerHTML;
        this.predictiveSearchResults.update(resultsMarkup);
      })
      .catch(error => {
        this.predictiveSearchResults.close();
        throw error;
      });
  }

  setSearchLimit() {
    const mobileMediaQuery = globalThis.matchMedia('(max-width: 1024px)');

    mobileMediaQuery.matches ? (this.searchLimit = 4) : (this.searchLimit = 5);

    mobileMediaQuery.addEventListener('change', event => {
      event.matches ? (this.searchLimit = 4) : (this.searchLimit = 5);
    });
  }

  /**
   * Closes the search panel when clicked outside
   * @param {Event} event The click event
   * @return {void}
   */
  conditionallyCloseSearchBar(event) {
    if (globalThis.location.pathname === '/search') return;
    if (event.target.closest('[data-predictive-search-bar]') === null) {
      this.closeSearchBar();
    }
  }

  /**
   * Add event to close the search panel when clicked outside
   * @return {void}
   */
  addCloseOnClickedOutsideListener() {
    this.mainContent.addEventListener(
      'click',
      this.conditionallyCloseSearchBar
    );
  }
}

customElements.get('predictive-search-bar') ||
  customElements.define('predictive-search-bar', PredictiveSearchBar);

export default PredictiveSearchBar;
